"use client";

/**
 * Client-side fetch hook for the parent + visible-subpages list used by both
 * NavbarRenderer (when "show subpages in nav" is on) and FooterRenderer (when
 * the pages column is on). A module-level promise cache dedupes calls across
 * sibling consumers AND across re-mounts within the same tab, so a single
 * public page render issues at most one round-trip per parent_page_id.
 */

import { useEffect, useState } from "react";
import { listFooterPagesPublic, type FooterPageSummary } from "../_logic/actions/page-actions";

/**
 * Keyed by `parent_page_id`. Stores either the in-flight promise (so concurrent
 * callers attach to the same request) or the resolved array (so subsequent calls
 * resolve synchronously). Cleared only on full page reload.
 */
const cache = new Map<number, Promise<FooterPageSummary[]> | FooterPageSummary[]>();

/** Resolve from the cache, falling back to a fresh server-action call. */
function loadFooterPages(parentPageId: number): Promise<FooterPageSummary[]> {
  const hit = cache.get(parentPageId);
  if (hit) return Promise.resolve(hit);
  const promise = listFooterPagesPublic(parentPageId)
    .then((rows) => {
      cache.set(parentPageId, rows);
      return rows;
    })
    .catch((error) => {
      cache.delete(parentPageId);
      throw error;
    });
  cache.set(parentPageId, promise);
  return promise;
}

interface UseFooterPagesPublicResult {
  pages: FooterPageSummary[];
}

/**
 * Returns the visible parent + subpages list for `parentPageId`. When `enabled`
 * is false, returns an empty array and skips the fetch (callers gate on the
 * public-vs-builder distinction this way).
 */
export function useFooterPagesPublic(parentPageId: number | null, enabled: boolean): UseFooterPagesPublicResult {
  const [pages, setPages] = useState<FooterPageSummary[]>(() => {
    if (!enabled || !parentPageId) return [];
    const hit = cache.get(parentPageId);
    return Array.isArray(hit) ? hit : [];
  });

  useEffect(() => {
    if (!enabled || !parentPageId) {
      setPages([]);
      return;
    }
    let cancelled = false;
    loadFooterPages(parentPageId)
      .then((rows) => {
        if (!cancelled) setPages(rows);
      })
      .catch((error) => console.error("useFooterPagesPublic: failed to load pages", { parentPageId }, error));
    return () => {
      cancelled = true;
    };
  }, [enabled, parentPageId]);

  return { pages };
}
